import { useState } from "react";
import {
  Copy,
  Trash2,
  Power,
  ChevronDown,
  ChevronRight,
  Lock,
  ArrowDownToLine,
  ArrowUpFromLine,
  ShieldCheck,
  Clock,
  RotateCcw,
  ScrollText,
  Sparkles,
  UserCheck,
  MousePointer2,
} from "lucide-react";
import type { HiveWorkflow, WfNode } from "@/types";
import { useWorkflow, permissionFootprint } from "@/store/workflowStore";
import { nodeDef, INTEGRATION_META, KIND_META } from "@/data/mockWorkflowNodes";
import {
  RiskBadge,
  Pill,
  Field,
  TextInput,
  SelectInput,
  Toggle,
} from "@/components/hive/shared";

export function NodeInspector({ wf }: { wf: HiveWorkflow }) {
  const selectedNodeId = useWorkflow((s) => s.selectedNodeId);
  const node = wf.nodes.find((n) => n.id === selectedNodeId) ?? null;

  if (!node) return <WorkflowOverviewInspector wf={wf} />;
  return <NodeConfig key={node.id} wf={wf} node={node} />;
}

/* ── Empty state: workflow-level summary + permission preview ──────────────── */

function WorkflowOverviewInspector({ wf }: { wf: HiveWorkflow }) {
  const fp = permissionFootprint(wf);
  const caps: { label: string; on: boolean }[] = [
    { label: "Modifies code", on: fp.code },
    { label: "Creates PRs", on: fp.pr },
    { label: "Posts externally", on: fp.external },
    { label: "Triggers deployments", on: fp.deploy },
    { label: "Spends model budget", on: fp.budget },
  ];
  return (
    <div className="flex h-full flex-col">
      <InspectorHeader title="Inspector" subtitle="Select a node to configure it" />
      <div className="flex-1 space-y-4 overflow-y-auto no-scrollbar p-4">
        <div className="grid place-items-center rounded-xl border border-dashed border-app bg-surface-2/30 py-8 text-center">
          <MousePointer2 size={20} className="text-app-2" />
          <div className="mt-2 text-xs text-app-2">Click any node on the canvas<br />to edit its configuration</div>
        </div>

        <Section title="Permissions Preview" icon={<Lock size={13} />}>
          <div className="space-y-2">
            <PermRow label="Reads from" items={fp.reads} />
            <PermRow label="Writes to" items={fp.writes} />
          </div>
          <div className="mt-3 space-y-1.5">
            {caps.map((c) => (
              <div key={c.label} className="flex items-center justify-between text-[11px]">
                <span className="text-app-1">{c.label}</span>
                <span className={c.on ? "font-medium text-amber-500" : "text-app-2"}>{c.on ? "Yes" : "No"}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Cost & Risk" icon={<Sparkles size={13} />}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-app-1">Risk level</span>
            <RiskBadge risk={wf.risk} />
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-center">
            <Tile label="Est. cost / run" value={`$${(wf.cost.model + wf.cost.cloud + wf.cost.verification).toFixed(2)}`} />
            <Tile label="BYONC savings" value={`$${wf.cost.byoncSavings.toFixed(2)}`} accent="#10b981" />
          </div>
        </Section>
      </div>
    </div>
  );
}

function PermRow({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-app-2">{label}</div>
      <div className="flex flex-wrap gap-1">
        {items.length ? (
          items.map((i) => (
            <span key={i} className="rounded-md border border-app bg-surface-2/60 px-1.5 py-0.5 font-mono text-[10px] text-app-1">
              {i}
            </span>
          ))
        ) : (
          <span className="text-[11px] text-app-2">—</span>
        )}
      </div>
    </div>
  );
}

/* ── Node configuration ───────────────────────────────────────────────────── */

function NodeConfig({ wf, node }: { wf: HiveWorkflow; node: WfNode }) {
  const def = nodeDef(node.defKey);
  const kind = KIND_META[def.kind];
  const integ = def.integration ? INTEGRATION_META[def.integration] : null;
  const setNodeConfig = useWorkflow((s) => s.setNodeConfig);
  const renameNode = useWorkflow((s) => s.renameNode);
  const toggleDisabled = useWorkflow((s) => s.toggleNodeDisabled);
  const toggleApproval = useWorkflow((s) => s.toggleNodeApproval);
  const duplicateNode = useWorkflow((s) => s.duplicateNode);
  const deleteNode = useWorkflow((s) => s.deleteNode);

  const cfg = (k: string, d = "") => String(node.config[k] ?? d);
  const set = (k: string, v: string | number | boolean) => setNodeConfig(node.id, k, v);

  return (
    <div className="flex h-full flex-col">
      <InspectorHeader
        title={kind.label}
        subtitle={integ ? integ.label : "Hive native"}
        color={integ?.color ?? kind.color}
        icon={<def.icon size={15} />}
      />
      <div className="flex-1 space-y-4 overflow-y-auto no-scrollbar p-4">
        {/* identity */}
        <div>
          <Field label="Node name">
            <TextInput value={node.title} onChange={(v) => renameNode(node.id, v)} />
          </Field>
          <p className="mt-2 text-[11px] leading-relaxed text-app-2">{def.description}</p>
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <Pill color={kind.color}>{kind.label}</Pill>
            <RiskBadge risk={def.risk} />
            {def.ai && <Pill color="#a855f7"><Sparkles size={9} /> AI-powered</Pill>}
            <Pill color={node.configComplete >= 0.6 ? "#22c55e" : "#f59e0b"} dot>
              {node.configComplete >= 0.6 ? "Configured" : "Needs config"}
            </Pill>
          </div>
        </div>

        {/* per-kind config */}
        {def.kind === "hook" && (
          <Section title="Hook configuration" icon={<def.icon size={13} />}>
            {integ && (
              <Field label="Integration">
                <div className="flex items-center gap-2 rounded-lg border border-app bg-surface-2/60 px-3 py-1.5 text-xs text-app">
                  <span className="h-2 w-2 rounded-full" style={{ background: integ.color }} /> {integ.label}
                </div>
              </Field>
            )}
            <div className="mt-2.5 space-y-2.5">
              <Field label="Event type">
                <SelectInput value={cfg("event", def.label)} onChange={(v) => set("event", v)} options={[def.label, "Any change", "Custom filter"]} />
              </Field>
              {def.integration === "jira" && (
                <Field label="Project key"><TextInput value={cfg("project", "PAY")} onChange={(v) => set("project", v)} /></Field>
              )}
              {def.integration === "github" && (
                <Field label="Repository"><TextInput value={cfg("repo", "checkout-service")} onChange={(v) => set("repo", v)} /></Field>
              )}
              {def.integration === "slack" && (
                <Field label="Channel"><TextInput value={cfg("channel", "#eng-alerts")} onChange={(v) => set("channel", v)} /></Field>
              )}
              <Field label="Priority filter">
                <SelectInput value={cfg("priority", "Any")} onChange={(v) => set("priority", v)} options={["Any", "High & Urgent", "Urgent only"]} />
              </Field>
              {def.key === "hook.schedule" && (
                <Field label="Frequency"><SelectInput value={cfg("frequency", "every 6h")} onChange={(v) => set("frequency", v)} options={["every 15m", "hourly", "every 6h", "daily", "weekly"]} /></Field>
              )}
              {def.key === "hook.webhook" && (
                <Field label="Webhook secret" hint="masked"><TextInput value={cfg("secret", "whsec_••••4f2a")} onChange={(v) => set("secret", v)} /></Field>
              )}
              <SampleEvent />
            </div>
          </Section>
        )}

        {def.kind === "process" && (
          <Section title="Process configuration" icon={<def.icon size={13} />}>
            <div className="space-y-2.5">
              {def.ai && (
                <Field label="Inference mode">
                  <SelectInput
                    value={cfg("inference", "BYONC · cost-optimized")}
                    onChange={(v) => set("inference", v)}
                    options={["BYONC · cost-optimized", "BYONC · local-preferred", "Cloud · highest quality", "Private only"]}
                  />
                </Field>
              )}
              <Field label="Input mapping" hint="from prior step"><TextInput value={cfg("input", "{{ previous.output }}")} onChange={(v) => set("input", v)} /></Field>
              <Field label="Output schema"><SelectInput value={cfg("schema", "auto")} onChange={(v) => set("schema", v)} options={["auto", "json", "markdown", "patch", "structured"]} /></Field>
              <div className="flex items-center justify-between rounded-lg border border-app bg-surface-2/40 px-3 py-2">
                <span className="text-[11px] text-app-1">Requires a workspace</span>
                <Toggle checked={cfg("workspace") === "true" || def.key === "proc.run-ares"} onChange={(v) => set("workspace", v)} color={kind.color} />
              </div>
              <Field label="Max runtime"><SelectInput value={cfg("maxRuntime", "15m")} onChange={(v) => set("maxRuntime", v)} options={["5m", "15m", "30m", "1h", "no limit"]} /></Field>
              <Field label="Retry policy"><SelectInput value={cfg("retry", "2× exponential")} onChange={(v) => set("retry", v)} options={["none", "1× immediate", "2× exponential", "3× exponential"]} /></Field>
              {def.key !== "proc.approval" && (
                <div className="flex items-center justify-between rounded-lg border border-app bg-surface-2/40 px-3 py-2">
                  <span className="flex items-center gap-1.5 text-[11px] text-app-1"><ShieldCheck size={12} /> Verification required</span>
                  <Toggle checked={cfg("verify") === "true" || def.key === "proc.run-ares"} onChange={(v) => set("verify", v)} color="#22c55e" />
                </div>
              )}
              <Field label="Human fallback"><SelectInput value={cfg("fallback", "ask reporter")} onChange={(v) => set("fallback", v)} options={["fail run", "ask reporter", "escalate to owner", "skip step"]} /></Field>
              {def.gate && (
                <div className="space-y-2.5 rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-2.5">
                  <div className="flex items-center gap-1.5 text-[11px] font-medium text-fuchsia-400"><UserCheck size={12} /> Approval gate</div>
                  <Field label="Approver"><TextInput value={cfg("approver", "tech-lead")} onChange={(v) => set("approver", v)} /></Field>
                  <Field label="Timeout"><SelectInput value={cfg("timeout", "4h")} onChange={(v) => set("timeout", v)} options={["30m", "1h", "4h", "1d"]} /></Field>
                  <Field label="On timeout"><SelectInput value={cfg("onTimeout", "escalate")} onChange={(v) => set("onTimeout", v)} options={["escalate", "auto-approve", "auto-reject", "hold"]} /></Field>
                </div>
              )}
            </div>
          </Section>
        )}

        {def.kind === "publish" && (
          <Section title="Publish configuration" icon={<def.icon size={13} />}>
            <div className="space-y-2.5">
              <Field label="Destination"><TextInput value={cfg("destination", defaultDest(def.integration))} onChange={(v) => set("destination", v)} /></Field>
              <Field label="Message / template"><textarea value={cfg("template", defaultTemplate(def.key))} onChange={(e) => set("template", e.target.value)} rows={3} className="w-full resize-none rounded-lg border border-app bg-surface-2 px-3 py-2 text-xs text-app outline-none focus:border-app-strong" /></Field>
              {def.integration === "artifact" && (
                <Field label="Artifact format"><SelectInput value={cfg("format", "json")} onChange={(v) => set("format", v)} options={["json", "markdown", "patch", "zip"]} /></Field>
              )}
              <Field label="Idempotency"><SelectInput value={cfg("idempotency", "by execution id")} onChange={(v) => set("idempotency", v)} options={["by execution id", "by content hash", "off"]} /></Field>
              {(def.deploy || def.pr) && (
                <Field label="Rollback behavior"><SelectInput value={cfg("rollback", "auto on failure")} onChange={(v) => set("rollback", v)} options={["auto on failure", "manual", "none"]} /></Field>
              )}
              <div className="flex items-center justify-between rounded-lg border border-app bg-surface-2/40 px-3 py-2">
                <span className="text-[11px] text-app-1">Dry-run in sandbox</span>
                <Toggle checked={cfg("dryRun", "true") === "true"} onChange={(v) => set("dryRun", v)} color={kind.color} />
              </div>
              {def.external && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 text-[11px] text-amber-500">
                  <Lock size={13} className="mt-0.5 shrink-0" /> Posts to an external surface — approval is enforced in production.
                </div>
              )}
            </div>
          </Section>
        )}

        {/* I/O + behavior */}
        <Section title="Inputs & Outputs" icon={<ArrowDownToLine size={13} />}>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div className="rounded-lg border border-app bg-surface-2/40 p-2">
              <div className="mb-1 flex items-center gap-1 text-app-2"><ArrowDownToLine size={11} /> Inputs</div>
              <div className="text-app-1">{def.reads?.length ? def.reads.join(", ") : "prior step output"}</div>
            </div>
            <div className="rounded-lg border border-app bg-surface-2/40 p-2">
              <div className="mb-1 flex items-center gap-1 text-app-2"><ArrowUpFromLine size={11} /> Outputs</div>
              <div className="text-app-1">{def.writes?.length ? def.writes.join(", ") : "result payload"}</div>
            </div>
          </div>
        </Section>

        <Section title="Reliability" icon={<RotateCcw size={13} />}>
          <Row icon={<Clock size={12} />} label="On timeout" value={cfg("onTimeout", "escalate")} />
          <Row icon={<RotateCcw size={12} />} label="Retry behavior" value={cfg("retry", "2× exponential")} />
          <Row icon={<Power size={12} />} label="On error" value={cfg("fallback", def.kind === "publish" ? "halt run" : "ask reporter")} />
          <Row icon={<ScrollText size={12} />} label="Audit logging" value="Enabled" valueColor="#22c55e" />
        </Section>

        {/* node actions */}
        <div className="space-y-2 border-t border-app pt-3">
          <div className="flex items-center justify-between rounded-lg border border-app bg-surface-2/40 px-3 py-2">
            <span className="flex items-center gap-1.5 text-[11px] text-app-1"><UserCheck size={12} /> Requires approval</span>
            <Toggle checked={!!node.approval} onChange={() => toggleApproval(node.id)} color="#a855f7" />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => toggleDisabled(node.id)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-app bg-surface-2/60 py-2 text-[11px] font-medium text-app-1 transition hover:border-app-strong">
              <Power size={12} /> {node.disabled ? "Enable" : "Disable"}
            </button>
            <button onClick={() => duplicateNode(node.id)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-app bg-surface-2/60 py-2 text-[11px] font-medium text-app-1 transition hover:border-app-strong">
              <Copy size={12} /> Duplicate
            </button>
            <button onClick={() => deleteNode(node.id)} className="flex items-center justify-center gap-1.5 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[11px] font-medium text-rose-500 transition hover:bg-rose-500/20">
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── building blocks ──────────────────────────────────────────────────────── */

function InspectorHeader({ title, subtitle, color = "#f43f5e", icon }: { title: string; subtitle: string; color?: string; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-app px-4 py-3">
      <div className="grid h-8 w-8 place-items-center rounded-lg text-white shadow" style={{ background: `linear-gradient(135deg, ${color}, ${color}aa)` }}>
        {icon ?? <MousePointer2 size={15} />}
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-app">{title}</div>
        <div className="truncate text-[11px] text-app-2">{subtitle}</div>
      </div>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-app bg-surface/40">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-3 py-2.5 text-left">
        <span className="text-app-2">{icon}</span>
        <span className="flex-1 text-[11px] font-semibold uppercase tracking-wider text-app-1">{title}</span>
        {open ? <ChevronDown size={14} className="text-app-2" /> : <ChevronRight size={14} className="text-app-2" />}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

function Row({ icon, label, value, valueColor }: { icon: React.ReactNode; label: string; value: string; valueColor?: string }) {
  return (
    <div className="flex items-center justify-between py-1 text-[11px]">
      <span className="flex items-center gap-1.5 text-app-1">{icon} {label}</span>
      <span className="font-medium capitalize" style={{ color: valueColor ?? "hsl(var(--text-0))" }}>{value}</span>
    </div>
  );
}

function Tile({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-lg border border-app bg-surface-2/40 py-2">
      <div className="text-sm font-semibold tabular-nums" style={{ color: accent ?? "hsl(var(--text-0))" }}>{value}</div>
      <div className="text-[10px] text-app-2">{label}</div>
    </div>
  );
}

function SampleEvent() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-app bg-surface-2/40">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-3 py-2 text-[11px] font-medium text-app-1">
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Sample event payload
      </button>
      {open && (
        <pre className="overflow-x-auto border-t border-app px-3 py-2 font-mono text-[10px] leading-relaxed text-app-2">{`{
  "id": "PAY-431",
  "type": "issue.created",
  "priority": "high",
  "project": "PAY",
  "reporter": "m.webb"
}`}</pre>
      )}
    </div>
  );
}

function defaultDest(integration?: string) {
  switch (integration) {
    case "slack": return "#eng-updates";
    case "jira": return "PAY board";
    case "github": return "momentum/checkout-service";
    case "email": return "stakeholders@momentum.ai";
    case "confluence": return "Engineering / Releases";
    default: return "";
  }
}
function defaultTemplate(key: string) {
  switch (key) {
    case "pub.slack.post": return "✅ {{ workflow.name }} finished for {{ task.id }} — {{ summary }}";
    case "pub.jira.update": return "Moved to In Review. PR: {{ pr.url }}";
    case "pub.github.pr": return "{{ task.title }}\n\n{{ summary }}\n\nVerified by ARES.";
    default: return "{{ summary }}";
  }
}
